<script lang="ts">
	import { afterUpdate, createEventDispatcher, getContext } from "svelte";
	import { portal } from "@xbs/lib-kanban";
	import type {
		DataStore,
		IColumn,
		IColumnShape,
		TAreasMeta,
		TEventDispatcher,
		TMenuItemsFn,
	} from "@xbs/lib-kanban";

	import Icon from "./../lib/Icon.svelte";
	import Menu from "./../lib/Menu.svelte";

	export let store: DataStore;
	export let contentEl: HTMLElement;
	export let column: IColumn;
	export let columnIndex: number;
	export let columns: IColumn[];
	export let areasMeta: TAreasMeta;

	const _ = getContext<any>("wx-i18n").getGroup("kanban");
	const dispatch: TEventDispatcher = createEventDispatcher();

	const { readonly, columnShape } = store.getReactive();
	$: edit = $readonly.edit;

	let renaming = false;
	let columnLabel = null;
	let columnEl: HTMLElement;

	function endRenaming() {
		if (renaming && columnLabel?.trim()) {
			dispatch("action", {
				action: "update-column",
				data: {
					id: column.id,
					column: {
						label: columnLabel,
					},
				},
			});
		}
		renaming = false;
		columnLabel = null;
	}

	function handleColumnRename() {
		if (!edit) {
			return;
		}
		renaming = true;
	}

	function handleColumnInput(e: any) {
		columnLabel = e.target.value;
	}

	function handleInputKeypress(e: any) {
		if (e.charCode === 13) {
			endRenaming();
		}
	}

	function focus(node: HTMLElement) {
		node.focus();
	}

	function handleColumnCollapse() {
		dispatch("action", {
			action: "update-column",
			data: {
				id: column.id,
				column: {
					collapsed: !column.collapsed,
				},
			},
		});
	}

	function handleMenuClick() {
		menuOpen = true;
	}

	function handleMove(dir: "left" | "right") {
		const columnIndex = columns.findIndex(c => c.id === column.id);
		const beforeIndex = dir === "left" ? columnIndex - 1 : columnIndex + 2;
		const before = columns[beforeIndex]?.id;

		dispatch("action", {
			action: "move-column",
			data: {
				id: column.id,
				before,
			},
		});
	}

	function handleMenuAction(ev: any) {
		const action = ev.detail.id;
		const menuItem = ev.detail.item;

		if (action === "add-card") {
			dispatch("action", {
				action: "add-card",
				data: {
					columnId: column.id,
				},
			});
		}
		if (action === "set-edit") {
			handleColumnRename();
		}
		if (action === "delete-column") {
			dispatch("action", {
				action: "delete-column",
				data: {
					id: column.id,
				},
			});
		}
		if (action === "move-column:left") {
			handleMove("left");
		}
		if (action === "move-column:right") {
			handleMove("right");
		}
		if (menuItem.onClick) {
			menuItem.onClick({
				id: action,
				item: menuItem,
				column,
			});
		}

		menuOpen = false;
	}

	const getMenuItems = (
		column: IColumn,
		columnIndex: number,
		columns: IColumn[],
		columnShape: IColumnShape
	) => {
		const menuItems = (
			columnShape.menu.items as TMenuItemsFn<{
				column: IColumn;
				columns: IColumn[];
				columnIndex: number;
			}>
		)({
			columns,
			columnIndex,
			column,
		});

		if (!menuItems || !menuItems.length) {
			return null;
		}

		return menuItems.map(item => {
			return {
				...item,
				label: _(item.label),
			};
		});
	};

	let menuCoords = null;
	let menuOpen = false;
	$: {
		if (menuOpen) {
			menuCoords = {
				x: columnEl.offsetLeft + columnEl.offsetWidth - 30,
				y: 45,
			};
		}
	}

	$: menuItems = getMenuItems(column, columnIndex, columns, $columnShape);
	$: isMenuVisible =
		$columnShape.menu.show && !!menuItems && edit && !renaming;

	let columnOffset = null;

	afterUpdate(() => {
		columnOffset = columnEl?.offsetLeft;
	});

</script>

<div
	bind:this={columnEl}
	class="wx-column"
	class:wx-over-limit={areasMeta[column.id].isOverLimit}
	class:wx-collapsed={column.collapsed}
	data-column-header={column.id}>
	{#if column.collapsed && columnEl && contentEl}
		<div
			class="wx-collapsed-column"
			style="left:{columnOffset}px;"
			on:click={() => handleColumnCollapse()}
			use:portal={{ container: contentEl }} />
	{/if}
	<div class="wx-collapse-icon">
		<Icon
			name={column.collapsed ? 'wxi-angle-right' : 'wxi-angle-left'}
			clickable
			click={() => handleColumnCollapse()} />
	</div>

	<div class="wx-label" on:dblclick={() => handleColumnRename()}>
		{#if renaming}
			<input
				type="text"
				class="wx-input"
				value={column.label}
				on:input={handleColumnInput}
				on:keypress={handleInputKeypress}
				on:blur={endRenaming}
				use:focus />
		{:else}{column.label}{/if}
		{#if isMenuVisible}
			{#if column.limit}
				({areasMeta[column.id].cardsCount}/{areasMeta[column.id].totalLimit})
			{/if}
			<div class="wx-menu">
				<Icon name="wxi-dots-h" click={() => handleMenuClick()} />
				{#if menuOpen}
					<Menu
						coords={menuCoords}
						data={menuItems}
						on:click={handleMenuAction}
						container={contentEl}
						cancel={() => {
							menuOpen = null;
						}} />
				{/if}
			</div>
		{/if}
	</div>

	{#if process.env.TRIALPACKAGE}
		<span
			class="wx-mark"
			class:wx-error={process.env.TRIALDATE < new Date()}>Trial</span>
	{/if}
</div>

<style>
	.wx-column {
		min-width: var(--wx-kanban-column-width);
		width: var(--wx-kanban-column-width);
		margin-left: var(--wx-padding);

		background: var(--wx-kanban-card-background);
		border: var(--wx-kanban-card-border);
		border-radius: var(--wx-kanban-card-border-radius);
		padding: 0 var(--wx-padding);
		display: flex;
		align-items: center;
		position: relative;
		z-index: 10;
	}
	.wx-column:last-child {
		margin-right: var(--wx-padding);
	}
	.wx-over-limit {
		border: solid var(--wx-kanban-over-limit-color) 1px;
	}
	.wx-collapsed {
		max-width: var(--wx-kanban-collapsed-column-width);
		width: var(--wx-kanban-collapsed-column-width);
		min-width: var(--wx-kanban-collapsed-column-width);
		overflow: hidden;

		display: flex;
		align-items: center;
		justify-content: center;
	}
	.wx-column.wx-collapsed .wx-label {
		display: none;
	}
	.wx-column.wx-collapsed .wx-menu {
		display: none;
	}
	.wx-collapse-icon:hover {
		background: var(--wx-background);
		cursor: pointer;
	}
	.wx-label {
		display: flex;
		align-items: center;

		font-weight: 500;
		font-size: var(--wx-font-size-md);
		overflow: hidden;
		text-overflow: ellipsis;
		width: 100%;
		height: 100%;
		margin-left: calc(var(--wx-padding) / 2);
	}
	.wx-label .wx-input {
		font-weight: 500;
	}
	.wx-input {
		padding: var(--wx-input-padding);
		outline: none;
		flex: 1;
		font: var(--wx-font);
		font-size: var(--wx-font-size-md);
		color: var(--wx-color-font);
		width: 100%;
		box-sizing: border-box;
		border: var(--wx-input-border);
		border-radius: var(--wx-input-border-radius);
		background-color: transparent;
	}
	.wx-input:focus {
		border: 1px solid var(--wx-color-primary);
	}

	.wx-mark {
		position: absolute;
		right: 34px;
		top: 12px;
		transform: rotate(30deg);
		color: #ccc;
		font-weight: 400;
		text-transform: uppercase;
	}
	.wx-mark.wx-error {
		color: red;
	}
	.wx-menu {
		position: absolute;
		top: calc(50% - 10px);
		right: 10px;
		cursor: pointer;
	}
	.wx-menu:hover {
		background: var(--wx-background);
	}
	.wx-collapsed-column {
		position: absolute;
		top: 50px;
		border-radius: 4px;
		width: var(--wx-kanban-collapsed-column-width);
		height: calc(100% - 50px);
		cursor: pointer;
		transition: background 0.2s ease;
	}
	.wx-collapsed-column:hover {
		background: var(--wx-border-color);
	}

</style>
